import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../../../firebase/config";
import { collection, query, where, onSnapshot, addDoc, orderBy } from "firebase/firestore";
import { useAuth } from "../../../context/AuthContext";
import { useTheme } from "../../../context/ThemeContext";
import Header from "../../../components/Header";
import { registrarAccion } from "../../../utils/trazabilidad";

export default function Pacientes() {
  const { currentUser, userRole, clinicaId, clinicaData } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [pacientes, setPacientes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busqueda, setBusqueda] = useState('');
  const [modalNuevo, setModalNuevo] = useState(false);
  const [nuevo, setNuevo] = useState({
    apellido: '', nombre: '', dni: '', nacionalidad: 'Argentina',
    fechaNacimiento: '', sexo: '', email: '', telefono: '',
    cobertura: 'Particular', numeroAfiliado: '',
    esMenor: false, tutorNombre: '', tutorDni: '', tutorRelacion: '', tutorEmail: '', tutorTelefono: ''
  });

  const canCreate = ['gestion_general', 'recepcion'].includes(userRole);
  const canEdit = ['gestion_general', 'recepcion'].includes(userRole);

  const btnPrimaryStyle = { background: `linear-gradient(135deg, ${theme.accent}, ${theme.accentDark})`, border: 'none', borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: '#FFFFFF', fontWeight: '600', fontFamily: "'Montserrat', sans-serif" };
  const btnSecondaryStyle = { background: 'transparent', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer', color: theme.secondary, fontFamily: "'Montserrat', sans-serif" };
  const inputStyle = { width: '100%', padding: '10px 14px', fontSize: '13px', border: `0.5px solid ${theme.borderInput}`, borderRadius: '8px', outline: 'none', color: theme.primary, background: theme.bgInput, boxSizing: 'border-box', fontFamily: "'Montserrat', sans-serif" };
  const labelStyle = { fontSize: '11px', color: theme.secondary, display: 'block', marginBottom: '6px', fontWeight: '500', letterSpacing: '1px' };
  const overlayStyle = { position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: theme.bgOverlay, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 };
  const modalStyle = { background: theme.bgModal, borderRadius: '16px', padding: '28px', width: '100%', maxWidth: '560px', maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.3)', border: `0.5px solid ${theme.border}` };

  function calcularEdad(fechaNacimiento) {
    if (!fechaNacimiento) return null;
    const hoy = new Date();
    const nac = new Date(fechaNacimiento + 'T12:00:00');
    let edad = hoy.getFullYear() - nac.getFullYear();
    const m = hoy.getMonth() - nac.getMonth();
    if (m < 0 || (m === 0 && hoy.getDate() < nac.getDate())) edad--;
    return edad;
  }

  useEffect(() => {
    if (!clinicaId) return;
    const q = query(collection(db, 'pacientes'), where('clinicaId', '==', clinicaId), orderBy('apellido'));
    const unsub = onSnapshot(q, snap => {
      setPacientes(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [clinicaId]);

  async function crearPaciente() {
    if (!nuevo.apellido || !nuevo.nombre || !nuevo.dni) return;
    await addDoc(collection(db, 'pacientes'), {
      ...nuevo,
      edad: calcularEdad(nuevo.fechaNacimiento),
      creadoEn: new Date(),
      clinicaId,
    });
    await registrarAccion(currentUser?.email, userRole, 'Pacientes', 'Crear paciente', `${nuevo.apellido}, ${nuevo.nombre} — DNI ${nuevo.dni}`, clinicaId);
    setModalNuevo(false);
    setNuevo({ apellido: '', nombre: '', dni: '', nacionalidad: 'Argentina', fechaNacimiento: '', sexo: '', email: '', telefono: '', cobertura: 'Particular', numeroAfiliado: '', esMenor: false, tutorNombre: '', tutorDni: '', tutorRelacion: '', tutorEmail: '', tutorTelefono: '' });
  }

  const pacientesFiltrados = pacientes.filter(p =>
    `${p.apellido} ${p.nombre}`.toLowerCase().includes(busqueda.toLowerCase()) ||
    p.dni?.includes(busqueda)
  );

  return (
    <div style={{ minHeight: '100vh', background: theme.bg, fontFamily: "'Montserrat', sans-serif" }}>
      <Header titulo="Pacientes" backLabel="Dashboard" backRuta="/dashboard" />
      <div style={{ padding: '24px', background: theme.bg }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px', gap: '12px', flexWrap: 'wrap' }}>
          <input
            value={busqueda} onChange={e => setBusqueda(e.target.value)}
            placeholder="Buscar por apellido, nombre o DNI..."
            style={{ ...inputStyle, maxWidth: '360px' }}
          />
          {canCreate && (
            <button onClick={() => setModalNuevo(true)} style={btnPrimaryStyle}>+ Nuevo paciente</button>
          )}
        </div>

        {loading
          ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px' }}>Cargando...</div>
          : pacientesFiltrados.length === 0
            ? <div style={{ textAlign: 'center', color: theme.secondary, padding: '48px', fontSize: '13px' }}>No se encontraron pacientes</div>
            : <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                {pacientesFiltrados.map(p => (
                  <div key={p.id} onClick={() => navigate(`/pacientes/${p.id}`)} style={{
                    background: theme.bgCard, borderRadius: '10px', padding: '14px 18px',
                    border: `0.5px solid ${theme.border}`, cursor: 'pointer',
                    display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '8px'
                  }}
                    onMouseEnter={e => e.currentTarget.style.borderColor = theme.accent}
                    onMouseLeave={e => e.currentTarget.style.borderColor = theme.border}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                      <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: theme.infoBg, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '15px', fontWeight: '700', color: theme.accent }}>
                        {p.apellido?.[0]}
                      </div>
                      <div>
                        <div style={{ fontSize: '14px', fontWeight: '600', color: theme.primary }}>{p.apellido}, {p.nombre}</div>
                        <div style={{ fontSize: '12px', color: theme.secondary }}>DNI {p.dni} · {p.cobertura}</div>
                      </div>
                    </div>
                    <div style={{ fontSize: '12px', color: theme.secondary }}>{p.edad ? `${p.edad} años` : ''}</div>
                  </div>
                ))}
              </div>
        }
      </div>

      {modalNuevo && (
        <div style={overlayStyle}>
          <div style={modalStyle}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <span style={{ fontSize: '16px', fontWeight: '600', color: theme.primary }}>Nuevo paciente</span>
              <button onClick={() => setModalNuevo(false)} style={{ background: 'none', border: 'none', fontSize: '20px', cursor: 'pointer', color: theme.secondary }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px' }}>
              <div><label style={labelStyle}>APELLIDO *</label><input value={nuevo.apellido} onChange={e => setNuevo({...nuevo, apellido: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>NOMBRE *</label><input value={nuevo.nombre} onChange={e => setNuevo({...nuevo, nombre: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>DNI *</label><input value={nuevo.dni} onChange={e => setNuevo({...nuevo, dni: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>NACIONALIDAD</label><input value={nuevo.nacionalidad} onChange={e => setNuevo({...nuevo, nacionalidad: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>FECHA DE NACIMIENTO</label><input type="date" value={nuevo.fechaNacimiento} onChange={e => setNuevo({...nuevo, fechaNacimiento: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>SEXO</label>
                <select value={nuevo.sexo} onChange={e => setNuevo({...nuevo, sexo: e.target.value})} style={inputStyle}>
                  <option value="">Seleccionar</option>
                  <option value="Masculino">Masculino</option>
                  <option value="Femenino">Femenino</option>
                  <option value="Otro">Otro</option>
                </select>
              </div>
              <div><label style={labelStyle}>EMAIL</label><input type="email" value={nuevo.email} onChange={e => setNuevo({...nuevo, email: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>TELÉFONO</label><input value={nuevo.telefono} onChange={e => setNuevo({...nuevo, telefono: e.target.value})} style={inputStyle} /></div>
              <div><label style={labelStyle}>COBERTURA</label>
                <select value={nuevo.cobertura} onChange={e => setNuevo({...nuevo, cobertura: e.target.value})} style={inputStyle}>
                  <option value="Particular">Particular</option>
                  <option value="Obra Social / Prepaga">Obra Social / Prepaga</option>
                </select>
              </div>
              <div><label style={labelStyle}>N° AFILIADO</label><input value={nuevo.numeroAfiliado} onChange={e => setNuevo({...nuevo, numeroAfiliado: e.target.value})} style={inputStyle} /></div>
              <div style={{ gridColumn: '1 / -1', display: 'flex', alignItems: 'center', gap: '8px' }}>
                <input type="checkbox" checked={nuevo.esMenor} onChange={e => setNuevo({...nuevo, esMenor: e.target.checked})} id="esMenor" />
                <label htmlFor="esMenor" style={{ fontSize: '13px', color: theme.secondary, cursor: 'pointer' }}>Paciente menor de edad</label>
              </div>
              {nuevo.esMenor && (
                <>
                  <div><label style={labelStyle}>TUTOR — NOMBRE</label><input value={nuevo.tutorNombre} onChange={e => setNuevo({...nuevo, tutorNombre: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>TUTOR — DNI</label><input value={nuevo.tutorDni} onChange={e => setNuevo({...nuevo, tutorDni: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>TUTOR — RELACIÓN</label><input value={nuevo.tutorRelacion} onChange={e => setNuevo({...nuevo, tutorRelacion: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>TUTOR — EMAIL</label><input value={nuevo.tutorEmail} onChange={e => setNuevo({...nuevo, tutorEmail: e.target.value})} style={inputStyle} /></div>
                  <div><label style={labelStyle}>TUTOR — TELÉFONO</label><input value={nuevo.tutorTelefono} onChange={e => setNuevo({...nuevo, tutorTelefono: e.target.value})} style={inputStyle} /></div>
                </>
              )}
            </div>
            <div style={{ display: 'flex', gap: '8px', marginTop: '20px' }}>
              <button onClick={crearPaciente} style={btnPrimaryStyle}>Guardar</button>
              <button onClick={() => setModalNuevo(false)} style={btnSecondaryStyle}>Cancelar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}